<script>
  import AbortController from 'abort-controller';
  import searchValue from '../../stores/search';
  import { openOverpanel } from '../../stores/overpanelControl';
  import { SPRITESHEET_URL } from '../../utils/constants.ts';
  import { KEYS } from '../../utils/constants';
  import search from '../../utils/search';
  import getClosest from '../../utils/closest-string';
  import UseClickOff from '../../utils/UseClickOff.svelte';
  import InterceptClick from '../../utils/InterceptClick.svelte';
  import { getSearchURL } from '../../utils/search-query.ts';
  import SearchOverpanelResults from '../SearchOverpanelResults/SearchOverpanelResults.svelte';
  import { trackEvent } from '../../utils/umami-events.js';

  let {
    parentComponent,
    inputPlaceholder,
    buttonText,
    searchingText,
    searchCompleteTemplate,
    searchInstructions,
    errorText,
    emptyErrorText,
    searchOverpanelResults,
    dataTestId = 'drug-search',
    lang = 'en',
  } = $props();

  const { ARROW_DOWN, ARROW_UP, ESCAPE } = KEYS;

  let searchResults = $state([]);
  let searchResultsLabel = $state();
  let previousResults = {};
  let controller;
  let isError = $state(false);
  let input = $state();
  let selected = $state();
  let inputFocused = $state(false);
  let container = $state();
  let resultsContainer = $state();
  let locationControl = $state();
  let resultTiles = [];
  let showInputEmptyMessage = $state(false);
  let typeaheadOpen = $derived(inputFocused || selected !== undefined);
  
  const resultsId = 'search-overpanel-results-list';

  searchValue.subscribe((value) => {
    selected = undefined;
    if (!value) searchResults.length = 0;
    if (searchResults.length) inputFocused = true;
  });

  // use time to only log complete search terms
  let timer, loggedValue;
  const handleAnalyticsEvent = () => {
    if (loggedValue !== $searchValue) {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        loggedValue = $searchValue;
        trackEvent(`${parentComponent}-drug-search`, { search: $searchValue });
      }, 1000);
    }
  };

  const handleSearch = async (e) => {
    const value = e.target.value;
    showInputEmptyMessage = false;

    if (controller) {
      controller.abort();
    }

    isError = false;

    if (!value) return;

    controller = new AbortController();

    let data;
    searchResultsLabel = searchingText;

    const res = await search({
      value: e.target.value,
      controller,
    }).catch((err) => {
      if (err.name !== 'AbortError') {
        return err;
      }
    });

    if (!res || res.status > 200) {
      return;
    }

    data = await res.json();

    if (data && data.hits) {
      // if results were returned cache them
      // if no results were returned find last closest match
      if (data.hits.length) {
        const emRegex = /<(\/?)em>/g;
        data.hits = data.hits.map((hit) => {
          try {
            hit.htmlName = hit._highlightResult.name.value.replace(emRegex, '<$1strong>');
            hit.htmlFormalName = hit._highlightResult.formalName.value.replace(emRegex, '<$1strong>');
          } catch {
            hit.htmlName = hit.name;
            hit.htmlFormalName = hit.formalName;
          }
          return hit;
        });
        previousResults[value] = data;
      } else {
        const closest = getClosest(value, Object.keys(previousResults));
        if (closest) {
          data = previousResults[closest];
        } else {
          isError = true;
        }
      }

      searchResults.length = 0;
      searchResults.push(...data.hits);

      if (searchResults.length) {
        searchResultsLabel = searchCompleteTemplate
          .replace(/{{NUMBER}}/, searchResults.length)
          .replace(/{{MEDICATION}}/, searchResults[0].name)
          .replace(/{{TOTAL}}/, searchResults.length);
      } else {
        searchResultsLabel = errorText;
      }
    }

    if ($searchValue.length > 3) {
      handleAnalyticsEvent();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let firstResult;

    firstResult = searchResults[selected || 0];

    if (!firstResult) {
      if (!$searchValue) showInputEmptyMessage = true;
      return;
    }

    const { slug, genericSlug, name } = firstResult;

    if (loggedValue !== $searchValue) {
      trackEvent(`${parentComponent}-drug-search`, { search: $searchValue });
    }
    
    window.location.href = getSearchURL({ slug, genericSlug, name, lang });
  };

  const handleFocus = () => {
    typeaheadOpen = true;
    if ($searchValue) {
      handleSearch({ target: { value: $searchValue } });
    }
  };

  const handleBlur = () => {
    showInputEmptyMessage = false;
    inputFocused = false;
    selected = undefined;
    typeaheadOpen = false;
  };

  const handleKeydown = (e) => {
    if (![ARROW_UP, ARROW_DOWN, ESCAPE].includes(e.key)) return;

    if (e.key === ESCAPE) {
      if (!$searchValue) {
        handleBlur();
        input.blur();
      } else {
        $searchValue = '';
      }

      e.stopPropagation();
      return;
    }

    if (!searchResults.length) return;

    if (selected === undefined) {
      selected = -1;
    }

    selected += e.key === ARROW_DOWN ? 1 : -1;
    if (selected < 0) selected = searchResults.length - 1;
    selected %= searchResults.length;

    e.preventDefault();
  };

  const handleExpandedClick = () => {
    if (input) input.focus();
  };
</script>

{#if typeaheadOpen}
  <UseClickOff {container} onClickOffHandler={handleBlur} />
  <InterceptClick parent={resultsContainer} target={locationControl} clickHandlerCallback={handleExpandedClick} />
{/if}

<div
  data-testid={dataTestId}
  class="border-12 border-transparent flex flex-row relative max-w-xl mx-auto md:hidden w-full md:max-w-full text-gray-gunmetal items-center md:z-10"
>
  <button
    class="w-full shadow-md rounded-xs border border-gray-gunmetal bg-white py-2 pl-10 pr-4 leading-loose
    text-base font-normal text-left placeholder-gray-warm cursor-text whitespace-no-wrap overflow-hidden"
    class:text-gray-gunmetal={$searchValue}
    class:text-gray-warm={!$searchValue}
    aria-label={buttonText}
    onclick={() => openOverpanel('search')}
  >
    {$searchValue || inputPlaceholder}
  </button>
  <svg class="absolute top-4 left-4 w-4 h-4 fill-current" role="presentation">
    <use xlink:href="{SPRITESHEET_URL}magnifying-glass" />
  </svg>
</div>
<div bind:this={container} class="group relative w-full hidden md:block">
  <form
    class="flex flex-row relative w-full md:max-w-full text-gray-gunmetal items-center md:border-12 md:rounded-t-lg
    md:z-10"
    class:md:border-transparent={!typeaheadOpen}
    class:md:border-blue-navy={typeaheadOpen}
    class:md:bg-blue-navy={typeaheadOpen}
    onsubmit={handleSubmit}
  >
    <div
      class="relative w-full flex-2"
      data-testid="{dataTestId}-combobox"
      role="combobox"
      aria-expanded={!!searchResults.length}
      aria-controls={resultsId}
      aria-owns={resultsId}
      aria-haspopup="listbox"
    >
      <div aria-live="assertive" aria-label={searchResultsLabel} data-testid="{dataTestId}-combobox-status"></div>
      <input
        class="hidden w-full rounded border border-gray-gunmetal bg-white py-2 pl-10 pr-4 leading-loose text-base font-normal
        placeholder-gray-warm md:inline focus:relative focus:outline-hidden focus:shadow-focus active:shadow-active active:relative"
        class:animate-shake={showInputEmptyMessage}
        data-testid="{dataTestId}-input"
        name="{dataTestId}-input"
        type="search"
        placeholder={inputPlaceholder}
        onfocus={handleFocus}
        oninput={handleSearch}
        onkeydown={handleKeydown}
        bind:this={input}
        bind:value={$searchValue}
        autocomplete="off"
        aria-autocomplete="list"
        aria-label={searchInstructions}
        aria-controls={resultsId}
        aria-activedescendant={selected !== undefined ? `search-result-${selected}` : ''}
      />
      <svg class="absolute top-4 left-4 w-4 h-4 fill-current" class:animate-shake={showInputEmptyMessage} role="presentation">
        <use xlink:href="{SPRITESHEET_URL}magnifying-glass" />
      </svg>
    </div>
    <button
      type="submit"
      class="flex-1 hidden md:block ml-3 rounded border-t border-blue-cerulean bg-blue-cerulean py-2 px-4
      text-center text-base leading-loose font-semibold text-white hover-hover:hover:bg-blue-darkish hover-hover:hover:border-blue-darkish
      active:bg-blue-navy active:border-blue-navy whitespace-nowrap focus:relative focus:outline-hidden
       focus:shadow-focus active:shadow-active active:relative"
      onfocus={handleFocus}
    >
      {buttonText}
    </button>
  </form>
  {#if showInputEmptyMessage}
    <div
      class="hidden md:block md:border-12 md:border-t-0"
      class:text-blue-navy={!typeaheadOpen}
      class:text-white={typeaheadOpen}
      class:bg-blue-navy={typeaheadOpen}
      class:md:border-blue-navy={typeaheadOpen}
      class:md:border-transparent={!typeaheadOpen}
      role="alert"
    >
      {emptyErrorText}
    </div>
  {/if}
  <div
    class="absolute bg-gray-mercury left-0 mt-0 z-40 w-full border-12 border-t-0 border-blue-navy rounded-b-lg"
    class:hidden={!typeaheadOpen}
    class:block={typeaheadOpen}
    bind:this={resultsContainer}
  >
    <SearchOverpanelResults
      {...searchOverpanelResults}
      id={resultsId}
      {searchResults}
      {resultTiles}
      {lang}
      {selected}
      {isError}
      {parentComponent}
      bind:locationControlElem={locationControl}
    />
  </div>
</div>
